/**
 * Order detail — Stage 1 drilldown surface.
 *
 *   - Reuses `/staff/api/orders/{id}/` (no new BI endpoint). The
 *     serializer is hand-rolled and ships a nested `{ order, user_info,
 *     totals, store, packer, rider, biller, ... }` shape. We mirror the
 *     fasto_staff Flutter client for field names so anything that works
 *     there continues to work here.
 *   - Renders a stage timeline coloured against `useSupportConfig()` SLAs.
 *   - Items table, customer card, store/staff card.
 *   - Rider trail uses an OpenStreetMap tile layer via Leaflet (see
 *     `components/maps/RiderTrailMap`) with a polyline of rider GPS pings
 *     and store/customer markers.
 */
import {
  ArrowLeft,
  Bike,
  ChevronRight,
  Clapperboard,
  LifeBuoy,
  MapPin,
  Package,
  Phone,
  Receipt,
  Store,
  User,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { RiderTrailMap } from '@/components/maps/RiderTrailMap';
import { OrderRemarksPanel } from '@/components/orders/OrderRemarksPanel';
import { useOrderDetail, useRiderLogs, type OrderDetail } from '@/api/legacy/orders';
import { useCrmTickets } from '@/api/bi/crm';
import { CrmCreateDrawer } from '@/components/crm/CrmCreateDrawer';
import {
  TICKET_STATUS_LABEL,
  TICKET_STATUS_TONE,
  TICKET_TYPE_LABEL,
} from '@/lib/crmStatus';
import { fmt } from '@/lib/format';
import { statusLabel, statusTone } from '@/lib/status';
import { bucketize, useSupportConfig, type SlaBucket } from '@/api/legacy/support';
import { cn } from '@/lib/cn';
import { slaTagLabel } from '@/lib/slaTags';
import { orderNeedsAutoRefundTicket } from '@/lib/refundTicket';
import type { CrmTicketType } from '@/api/bi/crm';

type StageKey =
  | 'created_timestamp'
  | 'packed_timestamp'
  | 'billed_timestamp'
  | 'picked_timestamp'
  | 'reached_timestamp'
  | 'completed_timestamp'
  | 'cancelled_timestamp'
  | 'return_timestamp';

const STAGE_ORDER: Array<{ key: StageKey; label: string }> = [
  { key: 'created_timestamp', label: 'Created' },
  { key: 'packed_timestamp', label: 'Packed' },
  { key: 'billed_timestamp', label: 'Billed' },
  { key: 'picked_timestamp', label: 'Picked' },
  { key: 'reached_timestamp', label: 'Reached' },
  { key: 'completed_timestamp', label: 'Completed' },
];

const SLA_TONE: Record<SlaBucket, string> = {
  'on-time': 'border-success/40 bg-success/5 text-success',
  soft: 'border-warning/40 bg-warning/5 text-warning',
  late: 'border-danger/40 bg-danger/10 text-danger',
};

function toNum(v: number | string | null | undefined): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

export function OrderDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const orderId = params.id ? Number(params.id) : null;
  const detail = useOrderDetail(orderId);
  const logs = useRiderLogs(orderId);
  const support = useSupportConfig();

  const data = detail.data;
  const order = data?.order ?? null;
  const userInfo = data?.user_info ?? null;
  const storeBlock = data?.store ?? null;
  const items = data?.order_items ?? [];

  const [crmDrawerOpen, setCrmDrawerOpen] = useState(false);
  const existingTickets = useCrmTickets({
    orderId: orderId ?? null,
    page: 1,
    pageSize: 50,
  });
  const linkedTickets = existingTickets.data?.results ?? [];

  /** Staff `late_tags` plus CRM auto-tickets (covers new tags if list API omits BI-only rows). */
  const mergedSlaTags = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ tag: string; late_duration?: number | null }> = [];
    const fromStaff = Array.isArray(detail.data?.late_tags) ? detail.data!.late_tags! : [];
    for (const t of fromStaff) {
      if (!t?.tag || seen.has(t.tag)) continue;
      seen.add(t.tag);
      out.push({ tag: t.tag, late_duration: t.late_duration });
    }
    for (const tk of linkedTickets) {
      const tag = tk.breach_tag?.tag;
      if (!tag || seen.has(tag)) continue;
      seen.add(tag);
      out.push({
        tag,
        late_duration: tk.breach_tag?.late_duration ?? null,
      });
    }
    return out;
  }, [detail.data?.late_tags, linkedTickets]);

  const stages = useMemo(() => {
    if (!order) {
      return [] as Array<{
        key: StageKey;
        label: string;
        ts: string | null;
        deltaSec: number | null;
        bucket: SlaBucket;
      }>;
    }
    let prevTs: number | null = null;
    return STAGE_ORDER.map(({ key, label }) => {
      const tsRaw = order[key as keyof typeof order] as string | null | undefined;
      const ts = tsRaw ? String(tsRaw) : null;
      const tsMs = ts ? new Date(ts).getTime() : null;
      const deltaSec = tsMs != null && prevTs != null ? Math.round((tsMs - prevTs) / 1000) : null;
      const { normal, hard } = stageBudget(key, support);
      const bucket = deltaSec != null ? bucketize(Math.abs(deltaSec), normal, hard) : 'on-time';
      if (tsMs != null) prevTs = tsMs;
      return { key, label, ts, deltaSec, bucket };
    });
  }, [order, support]);

  // Rider trail props derived from store + customer + GPS pings.
  const trail = useMemo<
    Array<{ latitude: number; longitude: number; timestamp: string | null }>
  >(() => {
    const raw = logs.data?.logs ?? [];
    const out: Array<{ latitude: number; longitude: number; timestamp: string | null }> = [];
    for (const p of raw) {
      const lat = toNum(p.latitude);
      const lng = toNum(p.longitude);
      if (lat == null || lng == null) continue;
      out.push({ latitude: lat, longitude: lng, timestamp: p.timestamp ?? null });
    }
    return out;
  }, [logs.data]);

  const storeLatLng = useMemo(() => {
    const lat = toNum(storeBlock?.latitude);
    const lng = toNum(storeBlock?.longitude);
    if (lat == null || lng == null) return null;
    return { latitude: lat, longitude: lng, label: storeBlock?.name ?? 'Store' };
  }, [storeBlock]);

  const customerLatLng = useMemo(() => {
    const lat = toNum(userInfo?.latitude);
    const lng = toNum(userInfo?.longitude);
    if (lat == null || lng == null) return null;
    return { latitude: lat, longitude: lng, label: userInfo?.name ?? 'Customer' };
  }, [userInfo]);

  const hasMapData = trail.length > 0 || !!storeLatLng || !!customerLatLng;
  const fromState =
    typeof (location.state as { from?: unknown } | null)?.from === 'string'
      ? String((location.state as { from?: string }).from)
      : null;
  const fromOrders = fromState ?? '/orders?tab=list';
  const handleBack = () => {
    if (fromState) navigate(fromState);
    else navigate(-1);
  };

  if (orderId == null) {
    return <ErrorState title="Missing order id" />;
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={handleBack} className="gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Button>
        <Link to={fromOrders} className="text-xs text-muted-foreground hover:text-foreground">
          Orders <ChevronRight className="inline h-3 w-3" /> #{orderId}
        </Link>
      </div>

      <PageHeader
        breadcrumbs
        eyebrow="Order"
        title={order ? `#${order.id}` : `#${orderId}`}
        description={
          order ? (
            <span className="inline-flex flex-wrap items-center gap-2">
              <Badge tone={statusTone(order.status)}>{statusLabel(order.status)}</Badge>
              <span className="text-xs text-muted-foreground">
                {storeBlock?.name ?? order.store ?? 'Unknown store'}
              </span>
              {mergedSlaTags.length > 0
                ? mergedSlaTags.map((t, i) => (
                    <Badge key={`${t.tag}-${i}`} tone="warning">
                      {slaTagLabel(t.tag)}
                    </Badge>
                  ))
                : null}
              {order.coupon_code ? <Badge tone="success">coupon {order.coupon_code}</Badge> : null}
              {data?.has_freeze_items ? <Badge tone="info">freeze items</Badge> : null}
              {linkedTickets.map((t) => (
                <Link
                  key={t.id}
                  to={`/crm/tickets/${t.id}`}
                  className="inline-flex items-center gap-1"
                >
                  <Badge tone={TICKET_STATUS_TONE[t.status]}>
                    {TICKET_TYPE_LABEL[t.type]} #{t.id}
                    {t.breach_tag?.tag ? ` · ${slaTagLabel(t.breach_tag.tag)}` : ''} ·{' '}
                    {TICKET_STATUS_LABEL[t.status]}
                  </Badge>
                </Link>
              ))}
              {orderNeedsAutoRefundTicket(order) &&
              !linkedTickets.some((t) => t.type === 'refund') ? (
                <Badge tone="primary">Refund ticket pending</Badge>
              ) : null}
            </span>
          ) : (
            'Loading…'
          )
        }
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCrmDrawerOpen(true)}
            className="gap-1"
            disabled={!orderId}
          >
            <LifeBuoy className="h-3.5 w-3.5" /> Create CRM ticket
          </Button>
        }
      />

      <CrmCreateDrawer
        open={crmDrawerOpen}
        onOpenChange={setCrmDrawerOpen}
        orderId={orderId}
        storeId={storeBlock?.id ?? null}
        customerId={userInfo?.id ?? null}
        defaultType={resolveDefaultTicketType(order, mergedSlaTags.length > 0)}
        defaultSummary={
          orderNeedsAutoRefundTicket(order)
            ? `Auto refund: order #${orderId} cancelled after ${order?.payment_type ?? 'online'} payment`
            : mergedSlaTags.length > 0
              ? `${slaTagLabel(mergedSlaTags[0]?.tag)} on order #${orderId}`
              : ''
        }
      />

      {detail.isError ? <ErrorState onRetry={() => void detail.refetch()} /> : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Timeline</CardTitle>
            {order ? <Badge tone="neutral">{fmt.npr(order.final_total)}</Badge> : null}
          </CardHeader>
          <CardBody>
            {detail.isLoading ? (
              <Skeleton className="h-44 w-full" />
            ) : (
              <ol className="relative space-y-3 border-l border-border pl-4">
                {stages.map((s) => (
                  <li key={s.key} className="relative">
                    <span
                      className={cn(
                        'absolute -left-[19px] top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-background',
                        s.ts ? 'bg-primary' : 'bg-muted-foreground/40',
                      )}
                      aria-hidden
                    />
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <div>
                        <div className="font-medium">{s.label}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {s.ts ? fmt.datetime(s.ts) : 'Pending'}
                        </div>
                      </div>
                      {s.deltaSec != null ? (
                        <span
                          className={cn(
                            'rounded-full border px-2 py-0.5 text-[11px]',
                            SLA_TONE[s.bucket],
                          )}
                          title={`${s.bucket} for this stage`}
                        >
                          +{fmt.duration(s.deltaSec)}
                        </span>
                      ) : null}
                    </div>
                  </li>
                ))}

                {order?.cancelled_timestamp ? (
                  <li className="relative">
                    <span
                      className="absolute -left-[19px] top-1.5 h-2.5 w-2.5 rounded-full bg-danger ring-2 ring-background"
                      aria-hidden
                    />
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <div>
                        <div className="font-medium text-danger">Cancelled</div>
                        <div className="text-[11px] text-muted-foreground">
                          {fmt.datetime(order.cancelled_timestamp)}
                          {order.cancellation_reason ? ` · ${order.cancellation_reason}` : ''}
                        </div>
                      </div>
                    </div>
                  </li>
                ) : null}

                {order?.return_timestamp ? (
                  <li className="relative">
                    <span
                      className="absolute -left-[19px] top-1.5 h-2.5 w-2.5 rounded-full bg-warning ring-2 ring-background"
                      aria-hidden
                    />
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <div>
                        <div className="font-medium text-warning">Return started</div>
                        <div className="text-[11px] text-muted-foreground">
                          {fmt.datetime(order.return_timestamp)}
                        </div>
                      </div>
                    </div>
                  </li>
                ) : null}
              </ol>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2">
              <Receipt className="h-4 w-4" /> Money
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            <KV
              label="Subtotal"
              value={data ? fmt.npr(data.totals.total_with_vat, true) : '—'}
            />
            <KV
              label="Final"
              value={order ? fmt.npr(order.final_total, true) : '—'}
              bold
            />
            {order?.coupon_code ? (
              <>
                <KV
                  label="Coupon"
                  value={
                    <span className="inline-flex items-center gap-1">
                      <Badge tone="success">{order.coupon_code}</Badge>
                      {order.coupon_title ? (
                        <span className="text-xs text-muted-foreground">{order.coupon_title}</span>
                      ) : null}
                    </span>
                  }
                />
                <KV
                  label="Discount"
                  value={`-${fmt.npr(order.coupon_discount ?? 0, true)}`}
                />
                {order.total_before_discount ? (
                  <KV
                    label="Before discount"
                    value={fmt.npr(order.total_before_discount, true)}
                  />
                ) : null}
              </>
            ) : null}
            <KV
              label="Delivery"
              value={order ? fmt.npr(order.delivery_fee, true) : '—'}
            />
            <KV
              label="Small cart"
              value={order ? fmt.npr(order.small_cart_fee, true) : '—'}
            />
            <KV
              label="Items"
              value={data ? `${data.totals.total_cart_items}` : '—'}
            />
            <KV
              label="Payment"
              value={
                <span className="inline-flex items-center gap-1">
                  {(order?.payment_type ?? '—').toString().toUpperCase()}
                  {order?.is_payment_done ? (
                    <Badge tone="success">paid</Badge>
                  ) : (
                    <Badge tone="warning">pending</Badge>
                  )}
                </span>
              }
            />
            {order?.distance != null ? (
              <KV
                label="Distance"
                value={`${(toNum(order.distance) ?? 0).toFixed(2)} km`}
              />
            ) : null}
          </CardBody>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2">
              <User className="h-4 w-4" /> Customer
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            {detail.isLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : userInfo ? (
              <>
                <div className="font-medium">{userInfo.name ?? '—'}</div>
                {userInfo.phone ? (
                  <div className="inline-flex items-center gap-1 text-muted-foreground">
                    <Phone className="h-3 w-3" />
                    <a href={`tel:${userInfo.phone}`} className="hover:underline">
                      {userInfo.phone}
                    </a>
                  </div>
                ) : null}
                {userInfo.address ? (
                  <div className="flex items-start gap-1 text-muted-foreground">
                    <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>{userInfo.address}</span>
                  </div>
                ) : null}
                {userInfo.id ? (
                  <Link
                    to={`/customers/${userInfo.id}`}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    Open customer 360 <ChevronRight className="h-3 w-3" />
                  </Link>
                ) : null}
              </>
            ) : (
              <div className="text-muted-foreground">No customer snapshot.</div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2">
              <Store className="h-4 w-4" /> Store & staff
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            <KV label="Store" value={storeBlock?.name ?? order?.store ?? '—'} />
            <KV
              label="Packer"
              value={
                <StaffName
                  name={data?.packer?.name ?? null}
                  phone={data?.packer?.phone ?? null}
                />
              }
            />
            <KV
              label="Biller"
              value={
                <StaffName
                  name={data?.biller?.name ?? null}
                  phone={data?.biller?.phone ?? null}
                />
              }
            />
            <KV
              label="Rider"
              value={
                data?.rider?.name ? (
                  <span className="inline-flex flex-wrap items-center justify-end gap-1">
                    <StaffName name={data.rider.name} phone={data.rider.phone ?? null} />
                    {data.rider.uses_cycle ? (
                      <Badge tone="info" className="gap-0.5">
                        <Bike className="h-3 w-3" /> cycle
                      </Badge>
                    ) : null}
                  </span>
                ) : (
                  '—'
                )
              }
            />
            {toNum(data?.order?.rider_distance) != null ? (
              <KV
                label="Rider distance"
                value={`${((toNum(data?.order?.rider_distance) ?? 0) / 1000).toFixed(2)} km`}
              />
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4" /> Locations
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            <KV
              label="Store"
              value={
                storeLatLng
                  ? `${storeLatLng.latitude.toFixed(5)}, ${storeLatLng.longitude.toFixed(5)}`
                  : '—'
              }
            />
            <KV
              label="Customer"
              value={
                customerLatLng
                  ? `${customerLatLng.latitude.toFixed(5)}, ${customerLatLng.longitude.toFixed(5)}`
                  : '—'
              }
            />
            <KV
              label="Rider pings"
              value={
                logs.isLoading
                  ? '…'
                  : trail.length > 0
                    ? `${trail.length}`
                    : '—'
              }
            />
          </CardBody>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="inline-flex items-center gap-2">
            <MapPin className="h-4 w-4" /> Rider trail
          </CardTitle>
          <span className="inline-flex items-center gap-2">
            {trail.length ? <Badge tone="info">{trail.length} pings</Badge> : null}
            {trail.length > 1 ? (
              <Link to={`/orders/${orderId}/replay`}>
                <Button
                  size="sm"
                  className="gap-1.5 bg-gradient-to-r from-[#ee2a4c] to-[#c01f3c] text-white shadow-md hover:opacity-90"
                >
                  <Clapperboard className="h-3.5 w-3.5" /> Watch delivery in 3D
                </Button>
              </Link>
            ) : null}
          </span>
        </CardHeader>
        <CardBody className="p-0">
          {detail.isLoading || logs.isLoading ? (
            <div className="p-4">
              <Skeleton className="h-72 w-full" />
            </div>
          ) : !hasMapData ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No rider GPS pings or store/customer coordinates available for this order.
            </div>
          ) : (
            <RiderTrailMap
              trail={trail}
              store={storeLatLng}
              customer={customerLatLng}
              height={380}
              className="overflow-hidden rounded-b-md"
            />
          )}
        </CardBody>
      </Card>

      <div className="mt-4">
        <OrderRemarksPanel
          packer={data?.packer?.remarks ?? null}
          biller={data?.biller?.remarks ?? null}
          rider={data?.rider?.remarks ?? null}
          customer={data?.user_info?.remarks ?? null}
        />
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="inline-flex items-center gap-2">
            <Package className="h-4 w-4" /> Items
          </CardTitle>
          {items.length ? <Badge tone="neutral">{items.length}</Badge> : null}
        </CardHeader>
        <CardBody className="p-0">
          {detail.isLoading ? (
            <div className="p-4">
              <Skeleton className="h-24 w-full" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No items recorded.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-border bg-muted/30 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2 font-semibold">Item</th>
                  <th className="px-4 py-2 text-right font-semibold">Qty</th>
                  <th className="px-4 py-2 text-right font-semibold">Price</th>
                  <th className="px-4 py-2 text-right font-semibold">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((it) => {
                  const sizeLabel =
                    it.size && it.measurement && Number(it.size) > 0
                      ? `${it.size} ${it.measurement}`
                      : null;
                  return (
                    <tr key={it.id} className="hover:bg-muted/20">
                      <td className="px-4 py-2">
                        <div>{it.product_name ?? `#${it.id}`}</div>
                        {sizeLabel ? (
                          <div className="text-[11px] text-muted-foreground">{sizeLabel}</div>
                        ) : null}
                      </td>
                      <td className="kpi-num px-4 py-2 text-right">{fmt.number(it.quantity ?? 0)}</td>
                      <td className="kpi-num px-4 py-2 text-right">
                        {fmt.npr(it.price_with_vat, true)}
                      </td>
                      <td className="kpi-num px-4 py-2 text-right font-medium">
                        {fmt.npr(it.total_with_vat, true)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {data?.totals ? (
                <tfoot>
                  <tr className="border-t border-border bg-muted/20 text-xs text-muted-foreground">
                    <td className="px-4 py-2" colSpan={3}>
                      Subtotal (incl. VAT)
                    </td>
                    <td className="kpi-num px-4 py-2 text-right font-medium text-foreground">
                      {fmt.npr(data.totals.total_with_vat, true)}
                    </td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          )}
        </CardBody>
      </Card>
    </>
  );
}

function KV({ label, value, bold }: { label: string; value: React.ReactNode; bold?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn('text-right', bold && 'font-semibold')}>{value}</span>
    </div>
  );
}

function StaffName({ name, phone }: { name: string | null; phone: string | null }) {
  if (!name) return <span>—</span>;
  return (
    <span className="inline-flex flex-col items-end">
      <span>{name}</span>
      {phone ? (
        <a href={`tel:${phone}`} className="text-[11px] text-muted-foreground hover:underline">
          {phone}
        </a>
      ) : null}
    </span>
  );
}

function stageBudget(key: StageKey, sc: ReturnType<typeof useSupportConfig>) {
  // Map a stage timestamp field to its corresponding SLA budget. We pick a
  // generous "normal" and the model's hard limit; missing budgets pass
  // through as null so the bucketizer treats the row as "on-time".
  switch (key) {
    case 'packed_timestamp':
      return { normal: sc.packing_normal_time, hard: sc.packing_limit_time };
    case 'billed_timestamp':
      return { normal: sc.biller_normal_time, hard: sc.biller_limit_time };
    case 'picked_timestamp':
      return { normal: sc.picking_time, hard: sc.picking_time };
    case 'reached_timestamp':
      return { normal: sc.rider_time, hard: sc.secondary_rider_time };
    case 'completed_timestamp':
      return { normal: sc.secondary_rider_time, hard: sc.tertiary_rider_time };
    default:
      return { normal: null, hard: null };
  }
}

function resolveDefaultTicketType(
  order: {
    status?: string | null;
    payment_type?: string | null;
    is_payment_done?: boolean | null;
  } | null,
  hasSlaTags: boolean,
): CrmTicketType {
  if (orderNeedsAutoRefundTicket(order)) return 'refund';
  if (hasSlaTags) return 'sla_breach';
  return 'customer_delay';
}

// Re-export the type so other modules continue to compile if they imported
// it transitively.
export type { OrderDetail };
